
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { convertBooleanToStatus } from "../../../../utils/ConverterUtils";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, prepareUploadFileData, sendToUploadFileApi } from "../../../../utils/ApiUtils";
import { useNavigate } from "react-router-dom";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { useBranch } from "../../../../context/BranchContext";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { ALL_WORK_DAYS, buildWorkingHours, sortWorkDays, toWorkingHoursPayload, workingDaysOf } from "../../../../utils/WorkDayUtils";

const EmployeeNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [status, setStatus] = useState(true);
    const [providesService, setProvidesService] = useState(true);
    // Nasce marcado: a restrição de clube é a exceção, não o padrão da casa.
    const [servesClubCustomers, setServesClubCustomers] = useState(true);
    const [portalAccessEnabled, setPortalAccessEnabled] = useState(false);
    const [entityData, setEntityData] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const navigate = useNavigate();

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        firstName: Yup.string()
            .min(3, t("validation.min.generic", { field: t('firstName'), qty: 3 }))
            .required(t('validation.required.firstName')),
        lastName: Yup.string()
            .min(3, t("validation.min.generic", { field: t('lastName'), qty: 3 }))
            .required(t('validation.required.lastName')),
        phone: Yup.string()
            .required(t('validation.required.phone')),
        // CPF obrigatório: é por ele que a API de integração identifica o colaborador, e um
        // cadastro sem CPF fica invisível para qualquer sistema externo.
        // nullable() antes do required: cadastro antigo vem sem CPF e o valor null tem que
        // cair na mensagem de campo obrigatório, não em erro de tipo do Yup.
        document: Yup.string()
            .nullable()
            .required(t('validation.required.document')),
        cnpj: Yup.string().test('cnpj-digits', 'CNPJ deve conter 14 dígitos.', value =>
            !value || value.replace(/\D/g, '').length === 14),
        gender: Yup.object().nullable().optional(),
        branches: Yup.array()
            .required(t('validation.required.branch'))
            .min(1, t('validation.required.branch')),
        lunchStartTime: Yup.string()
            .nullable()
            .test('lunch-time-pair', 'Informe o início do horário de almoço.', function (value) {
                return !value === !this.parent.lunchEndTime;
            }),
        lunchEndTime: Yup.string()
            .nullable()
            .test('lunch-time-pair', 'Informe o fim do horário de almoço.', function (value) {
                return !value === !this.parent.lunchStartTime;
            })
            .test('lunch-time-order', 'O fim do almoço deve ser depois do início.', function (value) {
                if (!value || !this.parent.lunchStartTime) return true;
                return value > this.parent.lunchStartTime;
            }),
        // Férias ligadas pedem as duas datas, com a volta depois da saída. Desligadas, as
        // datas ficam como estão e não são validadas: o backend guarda o último período
        // para o gestor religar sem redigitar, igual à resposta automática do Gmail.
        vacationStartDate: Yup.string()
            .nullable()
            .test('vacation-start', 'Informe a data de saída.', function (value) {
                return !this.parent.vacationEnabled || Boolean(value);
            }),
        vacationReturnDate: Yup.string()
            .nullable()
            .test('vacation-return', 'Informe a data de volta.', function (value) {
                return !this.parent.vacationEnabled || Boolean(value);
            })
            .test('vacation-order', 'A volta deve ser depois da saída.', function (value) {
                if (!this.parent.vacationEnabled || !value || !this.parent.vacationStartDate) return true;
                return value > this.parent.vacationStartDate;
            }),
        // Sem nenhum dia marcado o colaborador não teria agenda em dia nenhum, o que é
        // sempre um engano de preenchimento: para tirar alguém da agenda existe o
        // "Presta serviço?" e a inativação.
        workingHours: Yup.array()
            .test('ao-menos-um-dia', 'Marque pelo menos um dia de atendimento.',
                (value) => workingDaysOf(value).length > 0)
            // Horário pela metade não é aproveitável: ou o dia tem entrada e saída, ou
            // fica em branco e o colaborador acompanha o expediente da filial.
            .test('horario-completo', 'Informe a entrada e a saída do dia, ou deixe os dois em branco.',
                (value) => (value || []).every(item => !item.isWorking || !item.startTime === !item.endTime))
            .test('horario-ordem', 'A saída deve ser depois da entrada.',
                (value) => (value || []).every(item =>
                    !item.isWorking || !item.startTime || !item.endTime || item.endTime > item.startTime))
            .test('intervalo-completo', 'Informe o início e o fim do intervalo, ou deixe os dois em branco.',
                (value) => (value || []).every(item => !item.isWorking || !item.breakStartTime === !item.breakEndTime))
            .test('intervalo-ordem', 'O fim do intervalo deve ser depois do início.',
                (value) => (value || []).every(item =>
                    !item.isWorking || !item.breakStartTime || !item.breakEndTime
                    || item.breakEndTime > item.breakStartTime)),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            phone: '',
            gender: null,
            email: '',
            pixKey: '',
            document: '',
            cnpj: '',
            emergencyContactName: '',
            emergencyContactPhone: '',
            emergencyContactRelationship: '',
            birthDate: null,
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            zipCode: '',
            branches: selectedBranch ? [{ value: selectedBranch.id, label: selectedBranch.name }] : [],
            portalPassword: '',
            lunchStartTime: '',
            lunchEndTime: '',
            vacationEnabled: false,
            vacationStartDate: '',
            vacationReturnDate: '',
            // Novo colaborador nasce atendendo a semana toda, sem horário próprio: ele
            // acompanha o expediente da filial até alguém restringir.
            workingHours: buildWorkingHours(null, ALL_WORK_DAYS),
            serviceIds: [],
            planIds: []
        },
        enableReinitialize: true,
        validationSchema: formSchema,
        onSubmit: values => {
            let gender = null;

            if (values.gender !== null) {
                gender = values.gender.value;
            }

            // Filiais
            let branches = [];
            values.branches.forEach((item) => {
                branches.push({
                    id: item.value
                });
            });

            let entity = {
                id: getId(entityData),
                firstName: values.firstName,
                lastName: values.lastName,
                phone: values.phone,
                gender: gender,
                birthDate: values.birthDate ? formatDateToYYYYMMDD(values.birthDate) : null,
                status: convertBooleanToStatus(status),
                providesService: providesService,
                servesClubCustomers: servesClubCustomers,
                lunchStartTime: values.lunchStartTime || null,
                lunchEndTime: values.lunchEndTime || null,
                vacationEnabled: Boolean(values.vacationEnabled),
                vacationStartDate: values.vacationStartDate || null,
                vacationReturnDate: values.vacationReturnDate || null,
                // workDays continua no payload porque relatórios e o site ainda o leem; o
                // backend o mantém em sincronia com a grade ao salvar.
                workDays: sortWorkDays(workingDaysOf(values.workingHours)),
                workingHours: toWorkingHoursPayload(values.workingHours),
                serviceIds: values.serviceIds || [],
                planIds: values.planIds || [],
                portalAccessEnabled: portalAccessEnabled,
                portalPassword: values.portalPassword || null,
                // Os dois campos podem chegar nulos de um cadastro antigo, e o replace
                // direto no valor estouraria antes de o payload ser montado.
                document: (values.document || '').replace(/\D/g, ''),
                cnpj: (values.cnpj || '').replace(/\D/g, ''),
                emergencyContactName: values.emergencyContactName,
                emergencyContactPhone: values.emergencyContactPhone,
                emergencyContactRelationship: values.emergencyContactRelationship,
                email: values.email,
                pixKey: values.pixKey,
                branches: branches,
                address: {
                    street: values.street,
                    number: values.number,
                    complement: values.complement,
                    neighborhood: values.neighborhood,
                    city: values.city,
                    state: values.state,
                    zipCode: values.zipCode
                },
            }

            // Prepara os dados.
            prepareUploadFileData(uploadedFile, entity, "main/employees",
                entityData).then(data => {
                    // Aciona a api
                    sendToUploadFileApi(Api.main.employee + "/upload", data, entity.id, () => {
                        if (entityData == null) {
                            onReset();
                        } else {
                            navigate(MenuConfig.main.employee.route);
                        }
                    }, (error) => {
                        ApiError(error);
                        formik.setSubmitting(false);
                    });
                });
        },
    });

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const onReset = () => {
        formik.resetForm();

        // Se houver filial selecionada no contexto, restaura ela
        if (selectedBranch) {
            formik.setFieldValue('branches', [{ value: selectedBranch.id, label: selectedBranch.name }]);
        }

        // Força os campos customizados.
        setStatus(true);
        setProvidesService(true);
        setServesClubCustomers(true);
        setPortalAccessEnabled(false);
        setUploadedFile(null);
    }

    // Na criação contextual, a filial do header permanece fixa. Na edição,
    // o usuário pode revisar os vínculos entre o colaborador e suas filiais.
    const isBranchFixed = Boolean(selectedBranch && !entityData);

    return (
        <>
            <NewPage
                motherMenu={t('main')}
                childMenu={t('employees')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        status={status}
                        setStatus={setStatus}
                        providesService={providesService}
                        servesClubCustomers={servesClubCustomers}
                        setServesClubCustomers={setServesClubCustomers}
                        setProvidesService={setProvidesService}
                        portalAccessEnabled={portalAccessEnabled}
                        setPortalAccessEnabled={setPortalAccessEnabled}
                        entityData={entityData}
                        setUploadedFile={setUploadedFile}
                        uploadedFile={uploadedFile}
                        isBranchFixed={isBranchFixed}
                    />
                }
                setEntityData={setEntityData}
                path={Api.main.employee}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.main.employee.route}
            />
        </>
    );
}

export default EmployeeNew;
